import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown, ExternalLink, Loader2, Lock, RefreshCw } from 'lucide-react';
import { portal } from '../lib/api';

/**
 * The GitHub chip: a mark, a status dot, and a menu.
 *
 * The dot is the whole point of the control. Connecting is a round trip through
 * GitHub and back, so the first thing anyone wants to know on return is whether
 * it took — and a chip that answers that at a glance beats a settings page you
 * have to go and check. Green is connected, hollow is not.
 *
 * Picking a repository from the menu fills the source field rather than
 * submitting, because choosing what to build from and choosing to spend a
 * generation are two decisions and should stay two clicks.
 */

function GithubMark({ size = 15 }) {
  return (
    <svg viewBox="0 0 16 16" width={size} height={size} fill="currentColor" aria-hidden="true">
      <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38
        0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01
        1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95
        0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27
        2-.27s1.36.09 2 .27c1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82
        2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0
        .21.15.46.55.38A8.01 8.01 0 0 0 16 8c0-4.42-3.58-8-8-8Z" />
    </svg>
  );
}

function StatusDot({ on }) {
  return (
    <span
      aria-hidden="true"
      className={`inline-block w-[7px] h-[7px] rounded-full ${
        on ? 'bg-[#3fb950] shadow-[0_0_5px_rgba(63,185,80,0.85)]' : 'border border-[var(--text-4)]'
      }`}
    />
  );
}

function RepoMenu({ status, repos, busy, err, loadRepos, manage, disconnect, onPickRepo, setOpen }) {
  return (
      <div className="absolute z-30 mt-2 w-[22rem] max-w-[85vw] right-0 border border-[var(--border-2)] bg-[var(--surface)] rounded-sm shadow-xl">
        <div className="px-3 py-2 border-b border-[var(--border)] flex items-center justify-between">
          <span className="mono text-[0.66rem] tracking-[0.16em] uppercase text-[var(--text-3)]">
            {status.repo_selection === 'all' ? 'All repositories' : 'Selected repositories'}
          </span>
          <button type="button" onClick={loadRepos} title="Refresh"
            className="text-[var(--text-3)] hover:text-[var(--text)]">
            <RefreshCw size={12} />
          </button>
        </div>

        <div className="max-h-[16rem] overflow-y-auto">
          {busy && <div className="px-3 py-4 text-[0.85rem] text-[var(--text-3)]">Loading…</div>}
          {!busy && err && <div className="px-3 py-3 text-[0.85rem] text-[var(--rust)]">{err}</div>}
          {!busy && !err && repos && repos.length === 0 && (
            <div className="px-3 py-3 text-[0.85rem] text-[var(--text-3)]">
              Connected, but no repository is shared yet. Use “Add another
              repository” below and tick the one you want to build from.
            </div>
          )}
          {!busy && !err && (repos || []).map((r) => (
            <button
              key={r.full_name}
              type="button"
              onClick={() => { onPickRepo?.(r.html_url || `https://github.com/${r.full_name}`); setOpen(false); }}
              className="w-full text-left px-3 py-2 hover:bg-[var(--bg)] transition-colors border-b border-[var(--border)] last:border-b-0"
            >
              <div className="flex items-center gap-2">
                {r.private && <Lock size={11} className="text-[var(--text-4)] shrink-0" />}
                <span className="text-[0.88rem] text-[var(--text)] truncate">{r.full_name}</span>
              </div>
              {r.description && (
                <div className="text-[0.76rem] text-[var(--text-4)] truncate mt-0.5">{r.description}</div>
              )}
            </button>
          ))}
        </div>

        <div className="border-t border-[var(--border)] flex items-center justify-between px-3 py-2">
          {/* Named for what people actually come back to do. Access is granted
              one repository at a time, so "add another" is the second visit —
              "Manage on GitHub" did not say that. */}
          <button type="button" onClick={manage}
            className="mono text-[0.68rem] tracking-[0.12em] uppercase text-[var(--text-3)] hover:text-[var(--text)] inline-flex items-center gap-1.5">
            Add another repository <ExternalLink size={11} />
          </button>
          <button type="button" onClick={disconnect}
            className="mono text-[0.68rem] tracking-[0.12em] uppercase text-[var(--text-3)] hover:text-[var(--rust)]">
            Disconnect
          </button>
        </div>
      </div>
  );
}

export default function GithubConnect({
  onPickRepo, variant = 'chip', onNeedsAuth, onSingleRepo, attention = 0,
}) {
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [repos, setRepos] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [flashing, setFlashing] = useState(false);
  const boxRef = useRef(null);

  // The parent raises `attention` when a submission was refused because of a
  // repository this connection cannot read. Pointing at the control that fixes
  // it beats a sentence describing where it is.
  useEffect(() => {
    if (!attention) return undefined;
    setFlashing(true);
    const t = window.setTimeout(() => setFlashing(false), 2200);
    return () => window.clearTimeout(t);
  }, [attention]);

  const refresh = useCallback(() => {
    portal.githubStatus()
      .then(setStatus)
      // A logged-out visitor gets a 401, which is not "unavailable" — it is
      // "not signed in". On the landing page that distinction matters: the
      // control is there to tell someone the capability exists before they
      // have an account, so it must survive not knowing who they are.
      .catch((e) => setStatus(
        e?.response?.status === 401
          ? { available: true, connected: false, anonymous: true }
          : { available: false },
      ));
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Coming back from GitHub having just granted a repository, a list fetched
  // before that visit is stale. Refetch when the tab regains focus rather than
  // trusting whatever was loaded first.
  useEffect(() => {
    const onFocus = () => { refresh(); setRepos(null); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refresh]);

  // Close on an outside click or Escape — a menu that traps you is worse than
  // no menu.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Most people share exactly one repository — that is the whole shape of the
  // grant, one repo at a time. When there is only one, there is nothing to
  // choose between, and making someone open a menu to pick the single item in
  // it is a step that exists only because the code did not check.
  useEffect(() => {
    if (!onSingleRepo || !status?.connected) return;
    let cancelled = false;
    portal.githubRepos()
      .then((r) => {
        const list = r.repos || [];
        if (!cancelled) {
          setRepos(list);
          if (list.length === 1) {
            onSingleRepo(list[0].html_url || `https://github.com/${list[0].full_name}`,
                         list[0]);
          }
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.connected, status?.account]);

  const loadRepos = useCallback(() => {
    setBusy(true); setErr('');
    portal.githubRepos()
      .then((r) => setRepos(r.repos || []))
      .catch(() => setErr('Could not list your repositories.'))
      .finally(() => setBusy(false));
  }, []);

  const connect = async () => {
    setBusy(true); setErr('');
    try {
      const { url } = await portal.githubConnect();
      window.location.href = url;
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not start the GitHub connection.');
      setBusy(false);
    }
  };

  const manage = async () => {
    try {
      const { url } = await portal.githubManage();
      window.open(url, '_blank', 'noopener');
    } catch { /* the menu stays open; nothing destructive happened */ }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await portal.githubDisconnect();
      setRepos(null); setOpen(false); refresh();
    } finally { setBusy(false); }
  };

  // Nothing to offer if the server has no GitHub App registered. Showing a
  // control that cannot work is worse than showing none.
  if (!status || !status.available) return null;

  const connected = !!status.connected;
  const anonymous = !!status.anonymous;

  const onClick = () => {
    if (anonymous) return onNeedsAuth?.();
    if (!connected) return connect();
    setOpen((v) => !v);
    if (!repos) loadRepos();
    return undefined;
  };

  // Icon-only, sized to sit beside the paperclip in the landing composer. Same
  // menu underneath — the difference is only that the label is dropped, since
  // that row is icons and a word would not belong there.
  if (variant === 'icon') {
    return (
      <div className="relative inline-block" ref={boxRef}>
        <button
          type="button"
          onClick={onClick}
          disabled={busy}
          aria-label={connected ? `GitHub connected as ${status.account}` : 'Connect GitHub'}
          title={connected
            ? `GitHub connected as ${status.account} — pick a repository`
            : 'Connect GitHub to build from a private repository'}
          className={`relative shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-full border text-[var(--text-2)] hover:text-[var(--text)] transition-colors disabled:opacity-60 ${
            flashing
              ? 'border-[var(--accent)] gh-attention'
              : 'border-[var(--border-2)] hover:border-[var(--text-3)]'
          }`}
        >
          {busy ? <Loader2 size={16} className="animate-spin" /> : <GithubMark size={17} />}
          <span
            aria-hidden="true"
            className={`absolute bottom-[7px] right-[7px] w-[7px] h-[7px] rounded-full ring-2 ring-[var(--surface)] ${
              connected ? 'bg-[#3fb950] shadow-[0_0_5px_rgba(63,185,80,0.9)]' : 'bg-[var(--text-4)]'
            }`}
          />
        </button>
        {open && connected && <RepoMenu {...{ status, repos, busy, err, loadRepos, manage, disconnect, onPickRepo, setOpen }} />}
      </div>
    );
  }

  return (
    <div className="relative inline-block" ref={boxRef}>
      <button
        type="button"
        onClick={onClick}
        disabled={busy}
        title={connected ? `GitHub connected as ${status.account}` : 'Connect GitHub to build from a private repository'}
        className={`inline-flex items-center gap-2 px-3 py-2 rounded-sm border bg-[var(--bg)] text-[var(--text-2)] hover:text-[var(--text)] transition-colors disabled:opacity-60 ${
          flashing
            ? 'border-[var(--accent)] gh-attention'
            : 'border-[var(--border-2)] hover:border-[var(--text-4)]'
        }`}
      >
        {busy ? <Loader2 size={15} className="animate-spin" /> : <GithubMark />}
        <StatusDot on={connected} />
        <span className="mono text-[0.72rem] tracking-[0.1em] uppercase">
          {connected ? status.account || 'connected' : 'Connect GitHub'}
        </span>
        {connected && <ChevronDown size={13} className={open ? 'rotate-180 transition-transform' : 'transition-transform'} />}
      </button>

      {open && connected && (
        <RepoMenu {...{ status, repos, busy, err, loadRepos, manage, disconnect, onPickRepo, setOpen }} />
      )}
    </div>
  );
}

export { GithubMark, StatusDot };
